import { useCallback } from 'react'
import { useShallow } from 'zustand/react/shallow'
import { useDocumentState } from '@/state'
import queryClient from '@/queryClient'
import { QUERY_KEYS as TEMPLATE_ATTRIBUTE_QUERY_KEYS } from '@/services/queries/template_attributes/queryKeys'

import { TemplateAttribute } from '@/lib/api/client'

export const useMultiSelectKeydownHandler = () => {
  const setDocumentState = useDocumentState(
    useShallow((state) => state.setState),
  )

  const getDocumentState = useDocumentState(
    useShallow((state) => state.getState),
  )

  const handler = useCallback(
    (mouseEvent: MouseEvent, template: TemplateAttribute) => {
      const isSelectKeyPressed =
        mouseEvent.shiftKey || mouseEvent.ctrlKey || mouseEvent.metaKey

      if (!isSelectKeyPressed) {
        return
      }

      mouseEvent.stopPropagation()

      const bulkSelectedTemplateAttributes =
        getDocumentState().bulkSelectedTemplateAttributes

      // Verify currently selected template is in the bulk-selected template attributes array
      const selectedElement = getDocumentState().selectedElement

      const containsSelectedTemplate = Boolean(
        selectedElement &&
          bulkSelectedTemplateAttributes.find(
            (t) => t.id === selectedElement?.id,
          ),
      )

      // If the selected template is not in the bulk-selected array than add it
      if (!containsSelectedTemplate) {
        const templateAttributes = queryClient
          .getQueriesData({
            queryKey: [TEMPLATE_ATTRIBUTE_QUERY_KEYS.TEMPLATE_ATTRIBUTES],
          })
          .flatMap((kv) => kv[1])
          .filter((t) => !!t) as any[]

        const selectedTemplate = templateAttributes.find(
          (t) => t.id === selectedElement?.id,
        )

        if (selectedTemplate) {
          bulkSelectedTemplateAttributes.push(selectedTemplate)
        }
      }

      // Toggle template
      const hasTemplate = Boolean(
        bulkSelectedTemplateAttributes.find((t) => t.id === template.id),
      )

      setDocumentState({
        bulkSelectedTemplateAttributes: hasTemplate
          ? bulkSelectedTemplateAttributes.filter((t) => t.id !== template.id)
          : [...bulkSelectedTemplateAttributes, template],
      })
    },
    [getDocumentState, setDocumentState],
  )

  return handler
}
